import { Button, equalInLC, FCX } from "@datassential/platform-ui-lib";
import Modal from "@components/Modal/Modal";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Select from "@components/Select/Select";
import { AVAILABLE_COUNTRIES } from "@reducers/countries.reducer";
import { ICountry } from "@models";
import { countriesSelector } from "@selectors/countries.selector";
import http from "@core/http";
import apiConfig from "@apiConfig";
import Loader from "@components/Loader/Loader";
import { toast } from "react-toastify";

const TimeMachinePopup: FCX<{
    isOpen: boolean;
    onClose: () => void;
}> = ({
    isOpen,
    onClose,
}) => {
    const countries: ICountry[] = useSelector(countriesSelector);
    const [selectedCountryId, setSelectedCountryId] = useState("USA");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(1);
    /* @ts-ignore */
    const isLightTheme = useSelector((state) => state?.app?.isThemeLightEnable ?? false);

    const countriesOptions = useMemo(
        () => countries
            .filter(option => AVAILABLE_COUNTRIES.some(
                i => equalInLC(i, option.countryCode)
            ))
            .map(option => ({
                id: option.countryCode,
                name: option.countryCode,
                timeMachinePeriods: option.timeMachinePeriods || [],
            })),
        [countries]
    );

    const selectedCountry = useMemo(
        () => countriesOptions.find(option => equalInLC(option.id, selectedCountryId)),
        [countriesOptions, selectedCountryId]
    );

    const periodsOptions = useMemo(
        () => {
            const periods = selectedCountry?.timeMachinePeriods || [];
            periods.sort((a, b) => b.localeCompare(a));

            return periods.map((period, index) => ({
                id: index + 1,
                name: period,
            }));
        },
        [selectedCountry]
    );

    const selectedPeriod = useMemo(
        () => periodsOptions[selectedPeriodIndex - 1] || periodsOptions[0],
        [periodsOptions, selectedPeriodIndex]
    );

    const download = useCallback(
        () => {
            setIsLoading(true);
            http.downloadFileXHRFromUrl("GET", apiConfig.get('export time machine report', {
                period: selectedPeriod.name,
                countryCode: selectedCountryId,
            }))
                .then(() => {
                    toast.success("Report downloaded successfully");
                    onClose();
                })
                .catch((error) => {
                    toast.error(error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [selectedCountryId, selectedPeriod, onClose]
    )

    return (
        <Modal
            shown={isOpen}
            closeModal={onClose}
            hasOverlay
        >
            <div
                className="Popup__wrapper"
                onClick={(event) => event.stopPropagation() }>
                <div className="Popup">
                    <Loader isShown={isLoading}/>
                    <div className="Popup__title">
                        Download the Time Machine Report
                    </div>
                    <div className="Popup__content">
                        <Select
                            options={countriesOptions}
                            value={selectedCountryId}
                            title="Country"
                            onChange={(id: string) => {
                                setSelectedPeriodIndex(1);
                                setSelectedCountryId(id);
                            }}
                        />
                        <Select
                            options={periodsOptions}
                            value={selectedPeriodIndex}
                            title="Period"
                            onChange={setSelectedPeriodIndex}
                        />
                    </div>
                    <div className="Popup__btn-container">
                        <Button
                            modifiers={["naked"]}
                            style={{
                                color: isLightTheme ? undefined : "#fff",
                            }}
                            onClick={onClose    }
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={download}
                        >
                            Download
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default TimeMachinePopup;